import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const COLORS = [
  '#FFE742',
  '#FFA530',
  '#FF3366',
  '#FF69B4',
  '#00CC44',
  '#44FF88',
  '#0066FF',
  '#00CCFF',
  '#CC44FF',
  '#FF4444',
];
const GRAVITY = 0.18;

const getPieceW = (isCircle, isStrip) => {
  if (isCircle) return 4 + Math.random() * 4;
  if (isStrip) return 4 + Math.random() * 3;
  return 7 + Math.random() * 7;
};

const getPieceH = (isCircle, isStrip) => {
  if (isCircle) return 0;
  if (isStrip) return 18 + Math.random() * 14;
  return 7 + Math.random() * 7;
};

const lightenColor = (hex) => {
  const r = Math.min(255, parseInt(hex.slice(1, 3), 16) + 70);
  const g = Math.min(255, parseInt(hex.slice(3, 5), 16) + 70);
  const b = Math.min(255, parseInt(hex.slice(5, 7), 16) + 70);
  return `rgb(${r},${g},${b})`;
};

const makePiece = (cx, cy) => {
  const isCircle = Math.random() < 0.25;
  const isStrip = !isCircle && Math.random() < 0.45;
  const burstAngle = Math.random() * Math.PI * 2;
  const burstSpeed = 4 + Math.random() * 12;
  const color = COLORS[Math.floor(Math.random() * COLORS.length)];
  return {
    x: cx + (Math.random() - 0.5) * 60,
    y: cy + (Math.random() - 0.5) * 40,
    vx: Math.cos(burstAngle) * burstSpeed,
    vy: Math.sin(burstAngle) * burstSpeed - 4,
    w: getPieceW(isCircle, isStrip),
    h: getPieceH(isCircle, isStrip),
    isCircle,
    color,
    colorLight: lightenColor(color),
    angle: Math.random() * Math.PI * 2,
    spin: (Math.random() - 0.5) * 0.25,
  };
};

/**
 * ConfettiBurst - Canvas-based confetti that bursts outward from the center.
 * Triggers once each time `active` flips to true.
 *
 * @param {boolean} active - trigger the burst when flipped to true
 * @param {number} pieceCount - number of confetti pieces
 * @param {number} duration - animation duration in ms
 */
const ConfettiBurst = ({ active, pieceCount, duration }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    if (!canvas.width || !canvas.height) return undefined;
    const ctx = canvas.getContext('2d');

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const pieces = Array.from({ length: pieceCount }, () => makePiece(cx, cy));
    const startTime = performance.now();
    let animId;

    const draw = (now) => {
      const elapsed = now - startTime;
      const fadeOut = Math.max(0, 1 - (elapsed - duration * 0.7) / (duration * 0.3));
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = elapsed > duration * 0.7 ? fadeOut : 1;

      pieces.forEach((p) => {
        /* eslint-disable no-param-reassign */
        p.vy += GRAVITY;
        p.x += p.vx;
        p.y += p.vy;
        if (!p.isCircle) p.angle += p.spin;
        /* eslint-enable no-param-reassign */

        ctx.save();
        ctx.translate(p.x, p.y);
        if (p.isCircle) {
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.arc(0, 0, p.w, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.rotate(p.angle);
          // Squish width to simulate paper flipping in 3D
          const flipScale = Math.abs(Math.cos(p.angle * 2));
          const drawW = Math.max(1, p.w * flipScale);
          // Dark face
          ctx.fillStyle = p.color;
          ctx.fillRect(-drawW / 2, -p.h / 2, drawW, p.h);
          // Light face on top half to simulate fold highlight
          ctx.fillStyle = p.colorLight;
          ctx.fillRect(-drawW / 2, -p.h / 2, drawW, p.h / 2);
        }
        ctx.restore();
      });

      if (elapsed < duration) {
        animId = requestAnimationFrame(draw);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, [active, pieceCount, duration]);

  return <Canvas ref={canvasRef} aria-hidden="true" />;
};

ConfettiBurst.propTypes = {
  active: PropTypes.bool,
  pieceCount: PropTypes.number,
  duration: PropTypes.number,
};

ConfettiBurst.defaultProps = {
  active: false,
  pieceCount: 140,
  duration: 3500,
};

export default ConfettiBurst;

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;
